import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FaPlus, FaFire, FaTrash, FaUtensils, FaCheckCircle } from 'react-icons/fa';
import MacroEntryDialog from '@/components/MacroEntryDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('deisycfit_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function localTodayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const MACRO_COLORS = {
  calories: 'var(--brand-primary)',
  protein: '#00F0FF',
  carbs: '#CCFF00',
  fats: '#FF6B9D',
};

const MACRO_LABELS = {
  calories: { label: 'Kcal', suffix: '' },
  protein: { label: 'Proteína', suffix: 'g' },
  carbs: { label: 'Carbos', suffix: 'g' },
  fats: { label: 'Grasas', suffix: 'g' },
};

/**
 * Tracker diario de macros. Muestra Disponible vs Consumido vs Objetivo,
 * permite ingresar comidas extra y se reinicia cada día (filtra por fecha local).
 */
export default function DailyMacroTracker({ target, onTotalsChange }) {
  const today = localTodayStr();
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ calories: 0, protein: 0, carbs: 0, fats: 0 });
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/macro-log/today`, {
        params: { date_param: today },
        headers: authHeaders(),
      });
      setEntries(res.data?.entries || []);
      const t = res.data?.totals || { calories: 0, protein: 0, carbs: 0, fats: 0 };
      setTotals(t);
      onTotalsChange?.(t);
    } catch (e) {
      // no romper la página por esto
    } finally {
      setLoading(false);
    }
  }, [today, onTotalsChange]);

  const handleDelete = useCallback(async (entryId) => {
    if (!window.confirm('¿Eliminar esta entrada?')) return;
    try {
      await axios.delete(`${API}/macro-log/entries/${entryId}`, { headers: authHeaders() });
      fetchLog();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  }, [fetchLog]);

  const handleClear = useCallback(async () => {
    if (!window.confirm('¿Borrar TODAS las comidas extra de hoy?')) return;
    try {
      await axios.delete(`${API}/macro-log/today/clear`, {
        params: { date_param: today },
        headers: authHeaders(),
      });
      toast.success('Día reiniciado');
      fetchLog();
    } catch (e) {
      toast.error('No se pudo reiniciar');
    }
  }, [today, fetchLog]);

  const targetSafe = useMemo(() => ({
    calories: Number(target?.calories) || 0,
    protein: Number(target?.protein) || 0,
    carbs: Number(target?.carbs) || 0,
    fats: Number(target?.fats) || 0,
  }), [target]);
  const remaining = useMemo(() => ({
    calories: Math.round(targetSafe.calories - totals.calories),
    protein: Math.round(targetSafe.protein - totals.protein),
    carbs: Math.round(targetSafe.carbs - totals.carbs),
    fats: Math.round(targetSafe.fats - totals.fats),
  }), [targetSafe, totals]);

  const pct = useCallback((val, t) => {
    if (!t || t <= 0) return 0;
    return Math.min(100, Math.max(0, (val / t) * 100));
  }, []);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-8" data-testid="daily-macro-tracker">
      <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div>
            <h3 className="text-lg font-bold text-white uppercase flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <FaFire style={{ color: 'var(--brand-primary)' }} /> Macros Disponibles de Hoy
            </h3>
            <p className="text-xs text-gray-400 mt-1">
              Se reinicia automáticamente cada día. Registra comidas fuera de tu plan para llevar el control.
            </p>
          </div>
          <div className="flex gap-2">
            {entries.length > 0 && (
              <Button onClick={handleClear} variant="outline" className="border-red-500/30 text-red-300 hover:bg-red-500/10" size="sm" data-testid="clear-today-btn">
                Reiniciar día
              </Button>
            )}
            <Button onClick={() => setShowDialog(true)} className="text-white" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }} data-testid="open-add-macro-btn">
              <FaPlus className="mr-2" /> Ingresar Macros
            </Button>
          </div>
        </div>

        {/* Mini stats: Objetivo / Consumido / Disponible por macro */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['calories', 'protein', 'carbs', 'fats'].map((key) => {
            const t = targetSafe[key];
            const consumed = Math.round(totals[key] || 0);
            const rem = remaining[key];
            const over = rem < 0;
            const color = MACRO_COLORS[key];
            const meta = MACRO_LABELS[key];
            return (
              <div key={key} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid={`macro-card-${key}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400">{meta.label}</span>
                  <span className="text-[10px] text-gray-500">{t}{meta.suffix} obj.</span>
                </div>
                <p className={`text-2xl md:text-3xl font-extrabold leading-tight`} style={{ color: over ? '#EF4444' : color, fontFamily: 'Poppins, sans-serif' }}>
                  {rem}{meta.suffix}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">disponible</p>
                {/* Barra de progreso de consumido */}
                <div className="mt-2 h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct(consumed, t)}%`, background: over ? '#EF4444' : color }}
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  Consumido {consumed}{meta.suffix}{over ? ` (excedido ${Math.abs(rem)}${meta.suffix})` : ''}
                </p>
              </div>
            );
          })}
        </div>

        {/* Lista de entradas extra de hoy */}
        {entries.length > 0 && (
          <div className="mt-5 pt-5 border-t border-white/10">
            <p className="text-xs uppercase tracking-wider text-gray-400 mb-3">Comidas extra registradas hoy ({entries.length})</p>
            <div className="space-y-2">
              {entries.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 p-3 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                  data-testid={`entry-${e.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(var(--brand-primary-glow),0.15)' }}>
                      <FaUtensils className="text-pink-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium truncate">{e.name}</p>
                      <p className="text-xs text-gray-400">
                        {Math.round(e.calories)} kcal · {Math.round(e.protein)}g P · {Math.round(e.carbs)}g C · {Math.round(e.fats)}g G
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleDelete(e.id)} variant="outline" size="sm" className="border-red-500/30 text-red-300 hover:bg-red-500/10 flex-shrink-0">
                    <FaTrash />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="mt-4 text-center py-3 text-xs text-gray-500 flex items-center justify-center gap-2">
            <FaCheckCircle className="text-emerald-400" /> Sin comidas extra registradas hoy. Estás siguiendo tu plan al pie de la letra.
          </div>
        )}
      </div>

      {/* Diálogo "Ingresar Macros" con 3 pestañas (buscar / armar receta / manual) */}
      <MacroEntryDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        today={today}
        onSaved={() => fetchLog()}
      />
    </motion.div>
  );
}
